import { createAsyncThunk, createSlice, miniSerializeError, PayloadAction, SerializedError } from '@reduxjs/toolkit';
import type { RootState } from 'src/store';
import { resetStore } from './storeSlice';

import { ServerErrorAdapter } from '@itm/shared-frontend/lib/utils';

import { getTenantListAll } from 'src/api/clientPortal/tenant';

import { ServerError, TenantShortResponse } from 'src/types';

type TenantBranding = {
  tenantId: string | null;
  logoUrl: string | null;
  name: string | null;
  termsAndConditions: string | null;
};

type TenantListState = {
  data: TenantShortResponse[];
  isLoading: boolean;
  serverErrors: string[];
};

type TenantState = {
  branding: TenantBranding | null;
  tenantList: TenantListState;
};

type FetchTenantListActionParams = {
  // skip the request when the tenant list is already in the store or is being requested
  reuseExisting?: boolean;
};

const serializeError = (e: unknown): SerializedError => {
  const message = new ServerErrorAdapter(e as ServerError).combine();
  return miniSerializeError({ message });
};

export const fetchTenantListAction = createAsyncThunk<
  TenantShortResponse[],
  FetchTenantListActionParams,
  { state: RootState }
>(
  'tenant/fetchTenantListAction',
  async (_params, { signal }) => {
    const res = await getTenantListAll({ signal });
    return res.data;
  },
  {
    serializeError,
    condition: ({ reuseExisting }, { getState }) => {
      const { isLoading, data } = getState().tenant.tenantList;
      return !reuseExisting || (!isLoading && !data.length);
    },
  },
);

const initialState: TenantState = {
  branding: null,
  tenantList: { data: [], isLoading: false, serverErrors: [] },
};

const TenantSlice = createSlice({
  name: 'tenant',
  initialState,
  reducers: {
    setTenantBranding(state: TenantState, action: PayloadAction<TenantBranding | null>) {
      state.branding = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Tenant list
    builder.addCase(fetchTenantListAction.pending, (state: TenantState) => {
      state.tenantList = { ...initialState.tenantList, isLoading: true };
    });
    builder.addCase(fetchTenantListAction.fulfilled, (state: TenantState, action) => {
      state.tenantList.isLoading = false;
      state.tenantList.data = action.payload;
    });
    builder.addCase(fetchTenantListAction.rejected, (state: TenantState, action) => {
      state.tenantList.isLoading = false;
      if (action.meta.aborted) return;
      state.tenantList.serverErrors = action.error.message ? [action.error.message] : [];
    });
    // Cleanup
    // the branding is resolved by the domain before the sign in, so it must survive the reset
    builder.addCase(resetStore, (state: TenantState) => {
      state.tenantList = initialState.tenantList;
    });
  },
});

export const { setTenantBranding } = TenantSlice.actions;

export default TenantSlice.reducer;
